import { ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as SDK from 'azure-devops-extension-sdk';
import {
	CommonServiceIds,
	IProjectPageService,
} from 'azure-devops-extension-api';
import { createAuthClient } from 'src/http-client';
import AuthorizationPage from 'src/components/shared/authorization-app';
import { hasAzureAuthUserService } from 'src/services/appgami-api/find-azure-auth-service';

export interface ApplicationContext {
	projectId: string;
	userId: string;
	organizationName: string;
	organizationId: string;
	appToken: string;
	accessToken: string;
}

export interface AzureInitialization {
	isLoading: boolean;
	error?: Error;
	applicationContext?: ApplicationContext;
}

interface Props {
  children?: ReactNode;
}

interface AzureProviderValueType {
  applicationContext: ApplicationContext;
  error: Error | null;
  isLoading: boolean;
}

const AzureContext = createContext({} as AzureProviderValueType);

async function initSDK(): Promise<void> {
	SDK.init({ loaded: false, applyTheme: true });
	await SDK.ready();
	SDK.notifyLoadSucceeded();
}

async function getApplicationContext(): Promise<ApplicationContext> {
	const [token, user, accessToken, projectService, host] = await Promise.all([
		SDK.getAppToken(),
		SDK.getUser(),
		SDK.getAccessToken(),
		SDK.getService<IProjectPageService>(CommonServiceIds.ProjectPageService),
		SDK.getHost(),
	]);

	const project = await projectService.getProject();

	if (!project) throw new Error('Could not load current project');
	return {
		projectId: project.id,
		userId: user.id,
		organizationName: host.name,
		organizationId: host.id,
		appToken: token,
		accessToken: accessToken,
	};
}

function getErrorObject(error: unknown): Error {
	if (error instanceof Error) return error;
	return new Error('Error message not specified');
}

export const useAzureProvider = () => useContext(AzureContext);

export default function AzureProvider(props: Props) {
	const shouldInit = useRef(true);

	const [requestAuthorization, setRequestAuthorization] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);
	const [isReloading, setIsReloading] = useState(false);

	const [applicationContext, setApplicationContext] =
		useState<ApplicationContext | null>(null);

	const authClientInit = useCallback(async (
		args: {projectId: string; userId: string; token: string},
		options?: {
			onSuccess?: () => void;
			onError?: () => void;
		}) => {
		try {
			const hasAzureAuth = await hasAzureAuthUserService({
				userId: args.userId,
			});

			if (!hasAzureAuth) {
				// setRequestAuthorization(true);
				options?.onError?.();
			}

			if (hasAzureAuth) {
				setRequestAuthorization(false);
				setError(null);
				options?.onSuccess?.();
			}

			createAuthClient({
				token: args.token,
			});

		} catch (err) {
			console.error(err);
			throw new Error('Auth http client init error');
		}
	}, []);

	useEffect(() => {
		const init = async () => {
			try {
				await initSDK();
				const applicationContext = await getApplicationContext();
				setApplicationContext(applicationContext);
				await authClientInit({
					token: applicationContext.accessToken,
					projectId: applicationContext.projectId,
					userId: applicationContext.userId,
				});
				
			} catch (e) {
				setError(getErrorObject(e));
			} finally {
				setIsLoading(false);
			}
		};
	
		if (shouldInit.current) {
			shouldInit.current = false;
			init();
		}
	}, []);

	return (
		<AzureContext.Provider value={{
			error,
			isLoading,
			applicationContext: applicationContext as ApplicationContext,
		}}>
			{!isLoading && requestAuthorization && (
				<AuthorizationPage
					isReloading={isReloading}
					reloadtHttpAuthClientInit={async (
						input: {token: string, projectId: string; userId: string},
						options?: {onSuccess?: () => void; onError?: () => void}
					) => {
						setIsReloading(true);
						await authClientInit(input, options);
					}}
				/>
			)}
			{!isLoading && !requestAuthorization && !error && props.children}
		</AzureContext.Provider>
	);
}